import React from "react";

// Centroids inside a 300x300 viewBox for each of the 12 houses in North-Indian style.
// House 1 (Lagna) = top central rhombus; numbering proceeds counter-clockwise.
const HOUSE_CENTROIDS = {
  1:  { x: 150, y: 78 },
  2:  { x: 75,  y: 35 },
  3:  { x: 30,  y: 75 },
  4:  { x: 75,  y: 150 },
  5:  { x: 30,  y: 225 },
  6:  { x: 75,  y: 265 },
  7:  { x: 150, y: 222 },
  8:  { x: 225, y: 265 },
  9:  { x: 270, y: 225 },
  10: { x: 225, y: 150 },
  11: { x: 270, y: 75 },
  12: { x: 225, y: 35 },
};

const RASHIS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

const STROKE = "rgba(217, 119, 6, 0.45)";
const GOLD = "#B45309";
const TEXT = "#1A1410";

function tintFor(bindus) {
  if (bindus >= 35) return { fill: "rgba(16, 185, 129, 0.18)", text: "#065F46" };  // very strong (emerald)
  if (bindus >= 30) return { fill: "rgba(16, 185, 129, 0.10)", text: "#047857" };  // strong
  if (bindus >= 26) return { fill: "rgba(148, 163, 184, 0.10)", text: "#334155" }; // average
  if (bindus >= 20) return { fill: "rgba(245, 158, 11, 0.12)",  text: "#92400E" }; // weak
  return { fill: "rgba(244, 63, 94, 0.14)", text: "#9F1239" };                    // very weak
}

/**
 * SavChart — North-Indian style chart with Sarvashtakavarga total bindus
 * displayed prominently per house.
 *
 * Props:
 *   savByHouse: [{house, sign, bindus, verdict}, ...]   length=12, ordered H1..H12
 *   lagnaRashi: e.g. "Libra"
 *   size: pixels (default 340)
 */
export default function SavChart({ savByHouse, lagnaRashi, size = 340 }) {
  if (!savByHouse || savByHouse.length !== 12 || !lagnaRashi) return null;
  const lagnaIdx = RASHIS.indexOf(lagnaRashi);
  if (lagnaIdx < 0) return null;

  return (
    <svg
      viewBox="0 0 300 300"
      width={size}
      height={size}
      data-testid="sav-chart"
      style={{ background: "#FFFFFF" }}
    >
      {/* Tinted house-shape backgrounds — we shade the entire quadrant subtly
          by drawing 12 polygons aligned to the standard N-Indian layout. */}
      {/* The 4 outer triangles (H2 H12 H3 H11 H5 H9 H6 H8) and 4 inner diamonds (H1 H4 H7 H10) */}
      {/* For simplicity, just render the frame + tinted circles behind each centroid. */}
      <rect x="1" y="1" width="298" height="298" fill="none" stroke={STROKE} strokeWidth="1.5" />
      <line x1="0" y1="0" x2="300" y2="300" stroke={STROKE} strokeWidth="1.2" />
      <line x1="300" y1="0" x2="0" y2="300" stroke={STROKE} strokeWidth="1.2" />
      <polygon points="150,0 300,150 150,300 0,150" fill="none" stroke={STROKE} strokeWidth="1.2" />

      {/* Tinted background circles + bindus + sign label */}
      {savByHouse.map((cell) => {
        const c = HOUSE_CENTROIDS[cell.house];
        const tint = tintFor(cell.bindus);
        return (
          <g key={cell.house}>
            {/* tint circle behind bindus */}
            <circle cx={c.x} cy={c.y} r={22} fill={tint.fill} />
            {/* house number — top-left of cell */}
            <text
              x={c.x}
              y={c.y - 24}
              fill={GOLD}
              fontSize="9"
              fontWeight="500"
              textAnchor="middle"
              opacity="0.85"
              fontFamily="Outfit, sans-serif"
            >
              H{cell.house}
            </text>
            {/* big bindu number */}
            <text
              x={c.x}
              y={c.y + 4}
              fill={tint.text}
              fontSize="22"
              fontWeight="700"
              textAnchor="middle"
              fontFamily="DM Serif Display, serif"
            >
              {cell.bindus}
            </text>
            {/* sign abbreviation below */}
            <text
              x={c.x}
              y={c.y + 18}
              fill={TEXT}
              fontSize="9"
              fontWeight="500"
              textAnchor="middle"
              opacity="0.8"
              fontFamily="Outfit, sans-serif"
            >
              {cell.sign.slice(0, 3)}
            </text>
            {/* lagna marker on H1 */}
            {cell.house === 1 && (
              <text
                x={c.x}
                y={c.y + 32}
                fill={GOLD}
                fontSize="9"
                fontWeight="600"
                textAnchor="middle"
                fontFamily="Outfit, sans-serif"
              >
                Lg
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
